import type {Root} from 'hast'
import {defaultSchema, sanitize, type Schema} from 'hast-util-sanitize'
import mergeWith from 'lodash-es/mergeWith'

import {delimitersSanitizeSchema} from './extensions/delimiters'
import {distinguishAdjacentElementsSanitizeSchema} from './extensions/distinguish-adjacent-elements'

/**
 * Reimplements the Rails-side HTML pipeline sanitizer as a `hast-util-sanitize` schema.
 *
 * @see https://github.com/github/github/blob/master/lib/github/html.rb#L191
 */
const schema: Schema = mergeWith(
  {}, // use empty target object because mergeWith will mutate it

  defaultSchema,
  {
    allowComments: true,
    ancestors: {
      li: ['ol', 'ul'],
      dd: ['dl'],
      dt: ['dl'],
      summary: ['details'],
    },
    tagNames: ['video'],
    attributes: {
      video: ['controls', 'src', 'preload', 'playsinline', 'aria-label'],
    },
  },
  distinguishAdjacentElementsSanitizeSchema,
  delimitersSanitizeSchema,

  // By default `merge` will overwrite elements at matching indexes, but we want to concatenate the arrays instead
  (objValue, srcValue) => (Array.isArray(objValue) && Array.isArray(srcValue) ? objValue.concat(srcValue) : undefined),
)

// Disallow input elements entirely by removing them from the schema
if (schema.tagNames) {
  schema.tagNames = schema.tagNames.filter(tag => tag !== 'input')
}

/**
 * Sanitize an untrusted hast tree according to the GitHub HTML pipeline rules, while preserving custom `delimiter`
 * nodes.
 */
export function sanitizeHtml(tree: Root): Root {
  return sanitize(tree, schema) as Root
}
